/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { z } from "zod";

import { CSELanguageCode } from "./types";

import type {
	CosmosSearchFilterStrict,
	CosmosSearchItem,
	CosmosSearchResponse,
	CSELanguageCodeKeys,
	CSEMaskedLanguageCode,
	CSESearchPageMapAsset,
	GoogleDeResponse,
} from "./types";
import type { CosmosAssetFragment, SearchFilterFragment } from "@cosmos/hygraph-schema";

const domainsAllowed = new RegExp("(unrealengine.com|epicgames.com|twinmotion.com)");
const filenamesToSkip = new RegExp("(placeholder|logo|UE_Feed_Migration_Image|social-media-image)");

export const cseImagesToAsset = (cse_images?: CSESearchPageMapAsset[]): Partial<CosmosAssetFragment> | undefined => {
	if (
		cse_images &&
		cse_images?.length > 0 &&
		cse_images[0] &&
		cse_images[0].src &&
		domainsAllowed.test(cse_images[0].src) &&
		!filenamesToSkip.test(cse_images[0].src)
	) {
		const width = parseInt(cse_images[0].width || "");
		const height = parseInt(cse_images[0].height || "");
		return {
			url: cse_images[0].src,
			width: width > 0 ? width : undefined,
			height: height > 0 ? height : undefined,
		};
	}

	return undefined;
};

export const processItems = (data: GoogleDeResponse): CosmosSearchResponse => {
	if (!data?.results?.length) {
		return {
			items: [],
			total: 0,
		};
	}

	const items = data?.results?.filter((result) => result?.document?.derivedStructData?.title) || [];
	return {
		items: items?.map((result) => {
			const item = result?.document?.derivedStructData;
			return {
				id: result?.document?.id,
				title: { html: item?.htmlTitle, text: item?.title },
				href: item?.link,
				formattedHref: item?.htmlFormattedUrl,
				desc: item?.snippets?.[0].htmlSnippet,
				asset: cseImagesToAsset(item?.pagemap?.cse_image),
			} as CosmosSearchItem;
		}),
		total: items?.length || 0,
	};
};

export const getSiteSearchFilter = (
	filter?: string,
	defaultSearchFilter?: string | null,
	searchFilters?: SearchFilterFragment[] | null,
	defaultDomain?: string,
): string => {
	const defaultFilter = defaultSearchFilter || defaultDomain || "";

	if (!filter) {
		return defaultFilter;
	}

	const matchingFilter = (searchFilters || [])
		.filter((item): item is CosmosSearchFilterStrict => !!item.label && !!item.value)
		.find((item) => item.label.toLowerCase() === filter.toLowerCase());

	return matchingFilter?.value || defaultFilter;
};

export const searchEnvSchema = z.object({
	EPIC_SERVICE_GOOGLE_SEARCH_APP_ID: z.string(),
	VERTEX_SEARCH_API_KEY: z.string(),
	EPIC_SERVICE_GOOGLE_SEARCH_PROJECT_ID: z.string(),
	EPIC_SERVICE_GOOGLE_SEARCH_DOMAIN: z.string(),
});

export type SearchEnvSchema = z.infer<typeof searchEnvSchema>;

export const searchParamsSchema = z.object({
	query: z.string(),
	locale: z.string().default("en-US"),
	filter: z.string().optional(),
	slug: z.string(),
	preview: z.string().optional(),
});

export type SearchParamsSchema = z.infer<typeof searchParamsSchema>;

export interface VertexSearchFilterOptions {
	siteSearch?: string;
	languageRestrict?: CSEMaskedLanguageCode;
	countryRestrict?: string;
	fileType?: string;
}

// Filtering based on https://docs.cloud.google.com/generative-ai-app-builder/docs/filter-website-search
export const buildVertexSearchFilter = (options: VertexSearchFilterOptions): string | undefined => {
	if (!options) return undefined;

	const parts: string[] = [];

	if (options.siteSearch) {
		parts.push(`siteSearch:"${options.siteSearch}"`);
		// Multiple siteSearch filters don't work
		// Example from docs is either legacy or doesn't work on our project
		// (more info: https://docs.cloud.google.com/generative-ai-app-builder/docs/filter-website-search#examples-advanced-indexing)
		//
		// const sites = Array.isArray(options.siteSearch) ? options.siteSearch : [options.siteSearch];
		// const siteExpressions = sites.filter(Boolean).map((site) => `siteSearch:"${site}"`);
		// const expressionValue = sites.length > 1 ? `(${siteExpressions.join(" OR ")})` : siteExpressions[0];
		// if (expressionValue) {
		// 	parts.push(expressionValue);
		// }
	}

	if (options.languageRestrict) {
		parts.push(`lr:"${options.languageRestrict}"`);
	}

	if (options.countryRestrict) {
		parts.push(`cr:"${options.countryRestrict}"`);
	}

	if (options.fileType) {
		parts.push(`fileType:"${options.fileType}"`);
	}

	return parts.length > 0 ? parts.join(" AND ") : undefined;
};

const zhLocales = ["zh-TW", "zh-CN", "zh_TW", "zh_CN"];
export const processCSELocale = (s: string): CSELanguageCode => {
	const shortCode = zhLocales.includes(s) ? s.replace("-", "_") : s.slice(0, 2).toLowerCase();
	if (shortCode in CSELanguageCode) return CSELanguageCode[shortCode as CSELanguageCodeKeys];
	return CSELanguageCode.en;
};

export const getLanguageRestrict = (s: CSELanguageCode): CSEMaskedLanguageCode => `lang_${s}`;
